import { useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Trash2, Star } from "lucide-react";
import { api, useApi, errMsg, fmtDate, fmtCost } from "@/lib/api";
import { PageHeader, Card, Can, StatusBadge } from "@/components/common";
import { MessageTrace, EvalDialog } from "@/components/Trace";
import Markdown from "@/components/Markdown";
import { MODE_LABEL } from "@/pages/Conversations";
import { Button } from "@/components/ui/button";

export default function ConversationDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { data: c, reload } = useApi(`/conversations/${id}`);
  const [sel, setSel] = useState(null);
  const [evalMsg, setEvalMsg] = useState(null);
  if (!c) return <div className="text-muted-foreground">Chargement…</div>;
  const del = async () => {
    if (!window.confirm("Supprimer définitivement cette conversation ?")) return;
    try { await api.delete(`/conversations/${id}`); toast.success("Conversation supprimée"); nav("/historique"); } catch (e) { toast.error(errMsg(e)); }
  };
  const evalsOf = (mid) => c.evaluations.filter((e) => e.message_id === mid);
  return (
    <div>
      <Link to="/historique" className="text-sm text-muted-foreground flex items-center gap-1 mb-4 hover:text-foreground"><ArrowLeft className="h-4 w-4" />Historique</Link>
      <PageHeader title={`Conversation ${c.id.slice(0, 8)}`} subtitle={`${fmtDate(c.started_at)} · canal ${c.channel} · configuration ${MODE_LABEL[c.mode] || c.mode} · ${c.totals?.turns || 0} tours · ${fmtCost(c.totals?.cost_eur)}`}>
        <Can perm="conversations:delete"><Button variant="destructive" onClick={del} data-testid="delete-conv-btn"><Trash2 className="h-4 w-4 mr-1" />Supprimer</Button></Can>
      </PageHeader>
      <div className="flex flex-wrap gap-2 mb-4 text-xs">
        {(c.agents_used || []).map((a) => <StatusBadge key={a.agent_id + a.version} status="configuré" label={`${a.name} · ${a.version}`} />)}
        {(c.models_used || []).map((m) => <span key={m} className="mono border rounded px-2 py-0.5">{m}</span>)}
      </div>
      {c.call && <Card className="mb-4"><h3 className="font-semibold mb-2">Événements d’appel {c.call.simulated===false?'(Twilio réel)':'(simulé)'}</h3><div className="space-y-1 text-xs mono max-h-48 overflow-y-auto">{c.call.events.map((e, i) => <div key={i}>{fmtDate(e.at)} · {e.type} {e.text || e.reason || e.to || e.digit || ""}</div>)}</div></Card>}
      <div className="grid lg:grid-cols-[1fr_380px] gap-4">
        <Card className="space-y-3">
          {c.messages.map((m) => (
            <div key={m.id} className={`p-3 rounded-lg text-sm ${m.role === "user" ? "bg-primary/10" : "border cursor-pointer hover:border-primary/50"} ${sel?.id === m.id ? "ring-2 ring-primary" : ""}`} onClick={() => m.role === "assistant" && setSel(m)} data-testid={`history-msg-${m.role}`}>
              <div className="text-[11px] text-muted-foreground mb-1">{m.interrupted && <span className="text-amber-500 mr-1" data-testid="history-interrupted">[réponse audio interrompue]</span>}{m.role === "user" ? "Utilisateur" : `${m.agent_name} · ${m.agent_version} · ${m.model || "—"} · ${m.latency_ms} ms`} · {m.channel} · {fmtDate(m.created_at)}</div>
              {m.role === "user" ? <div className="whitespace-pre-wrap">{m.content}</div> : <Markdown>{m.content}</Markdown>}
              {m.role === "assistant" && (
                <div className="flex items-center gap-2 mt-2">
                  {evalsOf(m.id).map((e) => <span key={e.id} className="text-[11px] border rounded px-1.5" data-testid="history-eval-badge">{e.useful ? "👍" : "👎"} {e.rating}/5 {e.reasons.join(", ")}</span>)}
                  <Can perm="evaluations:write"><button className="text-[11px] text-muted-foreground hover:text-primary flex items-center gap-1" onClick={(ev) => { ev.stopPropagation(); setEvalMsg(m); }} data-testid="history-eval-btn"><Star className="h-3 w-3" />Évaluer</button></Can>
                </div>
              )}
            </div>
          ))}
        </Card>
        <Card className="h-fit sticky top-4"><h3 className="font-semibold mb-4">Trace</h3><MessageTrace m={sel} /></Card>
      </div>
      {evalMsg && <EvalDialog message={evalMsg} onClose={() => setEvalMsg(null)} onDone={reload} />}
    </div>
  );
}
